/**
 * Idioma & Voz/Audio · Sprint Corrección Final (Feb 2026)
 *
 * Dedicated screens (dedicated route, not sub-tab of Mi Cuenta) for
 * language selection and voice/audio preferences. Each screen owns its
 * URL so /perfil/idioma and /perfil/audio can be linked directly from
 * the avatar menu and side-nav.
 *
 * Persistence:
 *   - Language: localStorage['nexus.locale'] (already read by LocaleContext).
 *   - Audio: localStorage['nexus.audio'] — merged into the app when
 *     TTS / mic surfaces read it.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Volume2, Mic, Bell, Accessibility, Globe2, Play, Pause, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLocale, useT } from '../contexts/LocaleContext';
import { apiService } from '../services/api';

// ── Idioma ─────────────────────────────────────────────────────────
export default function IdiomaPage() {
  const nav = useNavigate();
  const { locale, setLocale } = useLocale();
  const t = useT();
  const langs: Array<{ code: 'es' | 'en'; name: string; native: string }> = [
    { code: 'es', name: 'Spanish', native: 'Español' },
    { code: 'en', name: 'English', native: 'English' },
  ];
  return (
    <PageShell title={t('idioma.title')} onBack={() => nav('/perfil')} testId="idioma-page">
      <div className="space-y-2" data-testid="idioma-list">
        {langs.map((l) => (
          <button
            key={l.code}
            onClick={() => { setLocale(l.code); toast.success(l.code === 'es' ? t('idioma.changedEs') : t('idioma.changedEn')); }}
            className="w-full text-left rounded-xl px-4 py-3 flex items-center gap-3 transition"
            style={{
              background: locale === l.code ? 'hsl(var(--accent) / 0.08)' : 'hsl(var(--bg-elev) / 0.7)',
              border: `1px solid ${locale === l.code ? 'hsl(var(--accent) / 0.4)' : 'hsl(var(--border))'}`,
            }}
            data-testid={`idioma-option-${l.code}`}
          >
            <Globe2 size={18} style={{ color: locale === l.code ? 'hsl(var(--accent))' : 'hsl(var(--text-muted))' }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{l.native}</div>
              <div className="text-xs" style={{ color: 'hsl(var(--text-muted))' }}>{l.name}</div>
            </div>
            {locale === l.code && <Check size={16} style={{ color: 'hsl(var(--accent))' }} />}
          </button>
        ))}
      </div>
      <p className="text-[11px] mt-4 leading-relaxed" style={{ color: 'hsl(var(--text-muted))' }}>
        {t('idioma.autoDetectHint')}
      </p>
    </PageShell>
  );
}

// ── Voz y Audio ────────────────────────────────────────────────────
type AudioPrefs = {
  voice_id: string;
  speed: number;
  auto_play: boolean;
  mic_enabled: boolean;
  system_sounds: boolean;
  high_contrast: boolean;
};

const DEFAULT_PREFS: AudioPrefs = {
  voice_id: 'nova',
  speed: 1.0,
  auto_play: false,
  mic_enabled: true,
  system_sounds: true,
  high_contrast: false,
};

function loadPrefs(): AudioPrefs {
  try {
    const raw = window.localStorage.getItem('nexus.audio');
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULT_PREFS;
}

function savePrefs(p: AudioPrefs) {
  try { window.localStorage.setItem('nexus.audio', JSON.stringify(p)); } catch { /* ignore */ }
}

export function AudioPage() {
  const nav = useNavigate();
  const t = useT();
  const [prefs, setPrefs] = useState<AudioPrefs>(loadPrefs());
  useEffect(() => { savePrefs(prefs); }, [prefs]);
  const set = <K extends keyof AudioPrefs>(k: K, v: AudioPrefs[K]) =>
    setPrefs((s) => ({ ...s, [k]: v }));
  return (
    <PageShell title={t('audio.title')} onBack={() => nav('/perfil')} testId="audio-page">
      <Section title={t('audio.sectionDefaultVoice')} icon={<Volume2 size={16} />}>
        <VoicePicker selected={prefs.voice_id} onSelect={(id) => set('voice_id', id)} />
      </Section>
      <Section title={t('audio.sectionSpeed')} icon={<Volume2 size={16} />}>
        <div className="flex items-center gap-3">
          <input
            type="range" min={0.5} max={2} step={0.1}
            value={prefs.speed}
            onChange={(e) => set('speed', Number(e.target.value))}
            className="flex-1 accent-current"
            style={{ accentColor: 'hsl(var(--accent))' }}
            data-testid="audio-speed"
          />
          <span className="tabular-nums text-sm w-12 text-right" style={{ color: 'hsl(var(--accent))' }}>
            {prefs.speed.toFixed(1)}x
          </span>
        </div>
      </Section>
      <Section title={t('audio.sectionToggles')} icon={<Volume2 size={16} />}>
        <Switch label={t('audio.switchAutoplay')} checked={prefs.auto_play} onChange={(v) => set('auto_play', v)} testId="audio-autoplay" />
        <Switch label={t('audio.switchMic')} icon={<Mic size={14} />} checked={prefs.mic_enabled} onChange={(v) => set('mic_enabled', v)} testId="audio-mic" />
        <Switch label={t('audio.switchSystemSounds')} icon={<Bell size={14} />} checked={prefs.system_sounds} onChange={(v) => set('system_sounds', v)} testId="audio-system-sounds" />
        <Switch label={t('audio.switchHiContrast')} icon={<Accessibility size={14} />} checked={prefs.high_contrast} onChange={(v) => set('high_contrast', v)} testId="audio-hicontrast" />
      </Section>
      <p className="text-[11px] mt-4 leading-relaxed" style={{ color: 'hsl(var(--text-muted))' }}>
        {t('audio.savedLocally')}
      </p>
    </PageShell>
  );
}

// ── Layout helpers ─────────────────────────────────────────────────
const PageShell: React.FC<{ title: string; onBack: () => void; children: React.ReactNode; testId?: string }> =
({ title, onBack, children, testId }) => {
  const t = useT();
  return (
  <div className="min-h-[100dvh]" style={{ background: 'hsl(var(--bg))', color: 'hsl(var(--text))' }} data-testid={testId}>
    <header className="sticky top-0 z-20 backdrop-blur-md"
      style={{
        background: 'linear-gradient(180deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))',
        borderBottom: '1px solid hsl(var(--border))',
      }}>
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} className="w-9 h-9 rounded-lg grid place-items-center"
          style={{ background: 'hsl(var(--bg-elev))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text-muted))' }}
          data-testid={`${testId}-back`} aria-label={t('common.back')}>
          <ArrowLeft size={16} />
        </button>
        <div className="text-xs uppercase tracking-[0.24em] font-semibold" style={{ color: 'hsl(var(--accent))' }}>
          {title}
        </div>
      </div>
    </header>
    <main className="max-w-2xl mx-auto px-4 py-6">{children}</main>
  </div>
  );
};

const Section: React.FC<{ title: string; icon?: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-widest font-semibold"
         style={{ color: 'hsl(var(--accent))' }}>
      {icon} {title}
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Switch: React.FC<{
  label: string; checked: boolean; onChange: (v: boolean) => void; testId?: string; icon?: React.ReactNode;
}> = ({ label, checked, onChange, testId, icon }) => (
  <button
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className="w-full flex items-center gap-3 rounded-xl px-4 py-3"
    style={{ background: 'hsl(var(--bg-elev) / 0.7)', border: '1px solid hsl(var(--border))' }}
  >
    <span className="text-sm flex-1 text-left flex items-center gap-2">
      {icon} {label}
    </span>
    <span
      className="w-10 h-5 rounded-full relative transition"
      style={{ background: checked ? 'hsl(var(--accent))' : 'hsl(var(--bg))', border: '1px solid hsl(var(--border))' }}
    >
      <span
        className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition"
        style={{ left: checked ? '22px' : '2px' }}
      />
    </span>
  </button>
);

// ── VoicePicker · Sprint UX Workspace v1.34 ────────────────────────
// Modern voice selector. Each voice is a card with:
//   • name + description
//   • Play button that immediately fetches a short sample and plays it
//   • Radio-style selection (only one active)
// Samples are cached in-memory per voice_id, so a second click on Play
// replays instantly without hitting the network again.
type Voice = { id: string; name: string; description?: string; personality?: string };

const VoicePicker: React.FC<{
  selected: string;
  onSelect: (id: string) => void;
}> = ({ selected, onSelect }) => {
  const t = useT();
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState<string | null>(null);
  const [sampleLoading, setSampleLoading] = useState<string | null>(null);
  const cache = useRef<Map<string, HTMLAudioElement>>(new Map());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getTTSVoices();
        if (!cancelled) setVoices(res.voices || []);
      } catch {
        // Fallback with hardcoded curated list so the UI never breaks.
        if (!cancelled) {
          setVoices([
            { id: 'jarvis', name: t('audio.fallbackJarvisName'), description: t('audio.fallbackJarvisDesc') },
            { id: 'nova',   name: t('audio.fallbackNovaName'),   description: t('audio.fallbackNovaDesc') },
            { id: 'fable',  name: t('audio.fallbackFableName'),  description: t('audio.fallbackFableDesc') },
          ]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      // Stop any playing sample when unmounting.
      cache.current.forEach((a) => { try { a.pause(); } catch { /* ignore */ } });
      cache.current.clear();
    };
  }, [t]);

  const play = useMemo(() => async (voiceId: string) => {
    // Stop any other sample already playing.
    cache.current.forEach((a, id) => {
      if (id !== voiceId) { try { a.pause(); a.currentTime = 0; } catch { /* ignore */ } }
    });
    let audio = cache.current.get(voiceId);
    if (!audio) {
      setSampleLoading(voiceId);
      try {
        const blob = await apiService.streamTts(t('audio.sample'), voiceId);
        const url = URL.createObjectURL(blob);
        audio = new Audio(url);
        audio.setAttribute('playsinline', 'true');
        audio.setAttribute('webkit-playsinline', 'true');
        audio.preload = 'auto';
        audio.onended = () => setPlaying((cur) => (cur === voiceId ? null : cur));
        audio.onpause = () => setPlaying((cur) => (cur === voiceId ? null : cur));
        cache.current.set(voiceId, audio);
      } catch (e: any) {
        toast.error(e?.response?.status === 402 ? t('audio.noCredits') : t('audio.sampleFail'));
        setSampleLoading(null);
        return;
      } finally {
        setSampleLoading(null);
      }
    }
    // Toggle play/pause.
    if (playing === voiceId) {
      audio.pause();
      setPlaying(null);
      return;
    }
    try {
      audio.currentTime = 0;
      await audio.play();
      setPlaying(voiceId);
    } catch (playErr: any) {
      if (playErr?.name === 'NotAllowedError') {
        toast.error(t('audio.retap'));
      } else {
        toast.error(t('audio.playFail'));
      }
      setPlaying(null);
    }
  }, [playing, t]);

  if (loading) {
    return (
      <div className="rounded-xl px-4 py-6 grid place-items-center" style={{ background: 'hsl(var(--bg-elev) / 0.5)', border: '1px solid hsl(var(--border))' }}>
        <Loader2 size={18} className="animate-spin" style={{ color: 'hsl(var(--accent))' }} />
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="audio-voice-picker">
      {voices.map((v) => {
        const active = selected === v.id;
        const isPlaying = playing === v.id;
        const isLoading = sampleLoading === v.id;
        return (
          <div
            key={v.id}
            data-testid={`audio-voice-card-${v.id}`}
            className="flex items-center gap-3 rounded-2xl px-3 py-3 transition-colors"
            style={{
              background: active ? 'hsl(var(--accent) / 0.10)' : 'hsl(var(--bg-elev) / 0.7)',
              border: `1px solid ${active ? 'hsl(var(--accent) / 0.5)' : 'hsl(var(--border))'}`,
            }}
          >
            {/* Play button (isolated so it doesn't select the voice) */}
            <button
              onClick={(e) => { e.stopPropagation(); play(v.id); }}
              disabled={isLoading}
              className="w-11 h-11 rounded-full grid place-items-center shrink-0 transition disabled:opacity-60"
              style={{
                background: isPlaying ? 'hsl(var(--accent))' : 'hsl(var(--bg))',
                border: `1px solid ${isPlaying ? 'hsl(var(--accent))' : 'hsl(var(--border))'}`,
                color: isPlaying ? 'hsl(210 40% 8%)' : 'hsl(var(--accent))',
              }}
              aria-label={isPlaying ? t('audio.pauseAria', { name: v.name }) : t('audio.playAria', { name: v.name })}
              data-testid={`audio-voice-play-${v.id}`}
            >
              {isLoading ? <Loader2 size={14} className="animate-spin" /> : isPlaying ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
            </button>

            {/* Name + description */}
            <button
              onClick={() => { onSelect(v.id); toast.success(t('audio.defaultSet', { name: v.name })); }}
              className="flex-1 min-w-0 text-left"
              data-testid={`audio-voice-select-${v.id}`}
            >
              <div className="text-sm font-medium flex items-center gap-1.5" style={{ color: active ? 'hsl(var(--accent))' : 'hsl(var(--text))' }}>
                {v.name}
                {active && <Check size={13} />}
              </div>
              {v.description && (
                <div className="text-[11px] leading-snug mt-0.5" style={{ color: 'hsl(var(--text-muted))' }}>
                  {v.description}
                </div>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
};
